import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { managerAPI, API_BASE_URL } from '../config/api';
import APP_COLOURS from '../config/colors';
import moment from 'moment';

const PublicProductivitySheet = () => {
  const { token } = useParams();
  const [productivityData, setProductivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [saving, setSaving] = useState(false);
  const [canvasRef, setCanvasRef] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState('');

  useEffect(() => {
    fetchProductivitySheet();
  }, [token]);

  const fetchProductivitySheet = async () => {
    try {
      setLoading(true);
      const data = await managerAPI.getPublicProductivitySheet(token);
      setProductivityData(data);
      setError('');
    } catch (err) {
      console.error('Error fetching productivity sheet:', err);
      setError(err.message || 'Failed to fetch productivity sheet. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(value);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl);
    alert('Link copied to clipboard!');
  };

  const handleOpenSignatureModal = (type) => {
    setSignatureType(type);
    setSignatureName('');
    setSignatureData('');
    setShowSignatureModal(true);
  };

  const initCanvas = (canvas) => {
    if (!canvas) return;
    setCanvasRef(canvas);
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const startDrawing = (e) => {
    if (!canvasRef) return;
    setIsDrawing(true);
    const rect = canvasRef.getBoundingClientRect();
    const ctx = canvasRef.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef) return;
    const rect = canvasRef.getBoundingClientRect();
    const ctx = canvasRef.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!canvasRef) return;
    setIsDrawing(false);
    const ctx = canvasRef.getContext('2d');
    ctx.closePath();
    // Save signature data
    setSignatureData(canvasRef.toDataURL());
  };

  const clearSignature = () => {
    if (!canvasRef) return;
    const ctx = canvasRef.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.width, canvasRef.height);
    setSignatureData('');
  };

  // Touch support for mobile
  const handleTouchStart = (e) => {
    if (!canvasRef) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.getBoundingClientRect();
    const ctx = canvasRef.getContext('2d');
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
  };

  const handleTouchMove = (e) => {
    if (!isDrawing || !canvasRef) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.getBoundingClientRect();
    const ctx = canvasRef.getContext('2d');
    ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
    ctx.stroke();
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  const handleSaveSignature = async () => {
    if (!signatureName.trim()) {
      alert('Please enter your full name');
      return;
    }

    if (!signatureData) {
      alert('Please draw your signature on the canvas');
      return;
    }

    try {
      setSaving(true);
      
      // Use the token-based signature endpoint (no authentication required)
      const response = await fetch(
        `${API_BASE_URL}/productivity-sheet/sign/${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signatureType: signatureType,
            signatureName: signatureName.trim(),
            signatureImage: signatureData
          })
        }
      );

      if (response.ok) {
        // Refresh data to show new signature
        await fetchProductivitySheet();
        setShowSignatureModal(false);
        setSignatureName('');
        setSignatureData('');
        alert('Signature saved successfully!');
      } else {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save signature');
      }
    } catch (err) {
      console.error('Error saving signature:', err);
      alert(err.message || 'Failed to save signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const groupSalesByDate = (sales) => {
    const grouped = {};
    
    sales.forEach(sale => {
      const dateKey = sale.dateCreated 
        ? moment(sale.dateCreated.toDate ? sale.dateCreated.toDate() : sale.dateCreated).format("YYYY-MM-DD")
        : moment(sale.date ? (sale.date.toDate ? sale.date.toDate() : sale.date) : new Date()).format("YYYY-MM-DD");
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(sale);
    });

    return Object.keys(grouped).sort().reverse().map(date => ({
      date,
      sales: grouped[date]
    }));
  };

  const renderDailyPerformanceRows = () => {
    if (!productivityData) return null;

    const { sales, attendance } = productivityData;
    
    const attendanceByDate = {};
    (attendance || []).forEach(att => {
      const dateKey = att.date 
        ? moment(att.date.toDate ? att.date.toDate() : att.date).format("YYYY-MM-DD")
        : att.dateCreated 
          ? moment(att.dateCreated.toDate ? att.dateCreated.toDate() : att.dateCreated).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");
      attendanceByDate[dateKey] = att;
    });

    const salesByDate = {};
    sales.forEach(sale => {
      const dateKey = sale.dateCreated 
        ? moment(sale.dateCreated.toDate ? sale.dateCreated.toDate() : sale.dateCreated).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");
      if (!salesByDate[dateKey]) {
        salesByDate[dateKey] = { trs: 0, ars: 0, newCustomer: 0, existCustomer: 0, count: 0 };
      }
      salesByDate[dateKey].trs += parseFloat(sale.trs || 0);
      salesByDate[dateKey].ars += parseFloat(sale.ars || 0);
      if (sale.newCustomer) salesByDate[dateKey].newCustomer += 1;
      else salesByDate[dateKey].existCustomer += 1;
      salesByDate[dateKey].count += 1;
    });

    const allDates = [...new Set([
      ...Object.keys(attendanceByDate),
      ...Object.keys(salesByDate)
    ])].sort().reverse();

    return allDates.map((dateKey, index) => {
      const att = attendanceByDate[dateKey] || {};
      const sale = salesByDate[dateKey] || { trs: 0, ars: 0, newCustomer: 0, existCustomer: 0, count: 0 };
      
      let hoursWorked = "--";
      if (att.clockIn && att.clockOut) {
        const checkin = moment(att.clockIn.toDate ? att.clockIn.toDate() : att.clockIn);
        const checkout = moment(att.clockOut.toDate ? att.clockOut.toDate() : att.clockOut);
        const workTime = checkout.diff(checkin, "minutes");
        
        let lunchDuration = 0;
        if (att.lunch_start && att.lunch_end) {
          const lunchStart = moment(att.lunch_start.toDate ? att.lunch_start.toDate() : att.lunch_start);
          const lunchEnd = moment(att.lunch_end.toDate ? att.lunch_end.toDate() : att.lunch_end);
          lunchDuration = lunchEnd.diff(lunchStart, "minutes");
        }
        
        const effectiveWorkTime = workTime - lunchDuration;
        if (effectiveWorkTime > 0) {
          hoursWorked = (effectiveWorkTime / 60).toFixed(2);
        }
      } else if (att.hoursWorked) {
        hoursWorked = parseFloat(att.hoursWorked).toFixed(2);
      }

      const avgARS = sale.count > 0 ? sale.ars / sale.count : 0;

      return (
        <tr key={dateKey} style={{
          backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9",
          borderBottom: "1px solid #eee",
        }}>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>{moment(dateKey).format("DD/MM")}</td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>{moment(dateKey).format("ddd")}</td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>
            {att.clockIn ? moment(att.clockIn.toDate ? att.clockIn.toDate() : att.clockIn).format("HH:mm") : "--"}
          </td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>
            {att.clockOut ? moment(att.clockOut.toDate ? att.clockOut.toDate() : att.clockOut).format("HH:mm") : "--"}
          </td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>{hoursWorked}</td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>{sale.newCustomer || 0}</td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>{sale.existCustomer || 0}</td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>
            {sale.trs ? formatCurrency(sale.trs) : "R0.00"}
          </td>
          <td style={{ padding: 3, color: "#000", fontSize: 8 }}>
            {avgARS ? formatCurrency(avgARS) : "R0.00"}
          </td>
        </tr>
      );
    });
  };

  const renderAuditTrail = () => {
    if (!productivityData) return null;

    const groupedByDate = groupSalesByDate(productivityData.sales);

    return groupedByDate.map(dayGroup => {
      const dayTotal = dayGroup.sales.reduce((sum, sale) => sum + parseFloat(sale.trs || 0), 0);
      const dayItemCount = dayGroup.sales.reduce((sum, sale) => sum + (sale.items?.length || 0), 0);

      return (
        <div key={dayGroup.date} style={{ marginBottom: 15, pageBreakInside: "avoid" }}>
          <div style={{
            backgroundColor: "#e8f4f8",
            padding: 8,
            marginBottom: 5,
            borderLeft: "4px solid #0068ff",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 11, color: "#000" }}>
                {moment(dayGroup.date).format("dddd, DD MMMM YYYY")}
              </strong>
              <div style={{ fontSize: 10, color: "#000" }}>
                {dayGroup.sales.length} Receipts • {dayItemCount} Items • {formatCurrency(dayTotal)}
              </div>
            </div>
          </div>

          {dayGroup.sales.map((sale, idx) => (
            <div key={idx} style={{
              marginLeft: 15,
              marginBottom: 10,
              borderLeft: "2px solid #ddd",
              paddingLeft: 10,
            }}>
              <div style={{
                backgroundColor: "#f5f5f5",
                padding: 5,
                marginBottom: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <div>
                  <strong style={{ color: "#000", fontSize: 10 }}>
                    Receipt #{idx + 1}: {sale.sale_id?.slice(0, 20) || 'N/A'}
                  </strong>
                  <span style={{ color: "#666", fontSize: 9, marginLeft: 10 }}>
                    {moment(sale.dateCreated?.toDate ? sale.dateCreated.toDate() : sale.dateCreated).format("HH:mm")}
                  </span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <strong style={{ color: "#000", fontSize: 10 }}>
                    {formatCurrency(sale.trs || 0)}
                  </strong>
                  <span style={{ color: "#666", fontSize: 9, marginLeft: 10 }}>
                    {sale.items?.length || 0} items
                  </span>
                </div>
              </div>

              <div style={{ padding: "5px 0", fontSize: 9, color: "#666" }}>
                <span style={{ marginRight: 15 }}>
                  Location: {sale.mall?.title || 'N/A'} - {sale.retailer?.title || 'N/A'}
                </span>
                <span style={{ marginRight: 15 }}>
                  {sale.newCustomer ? 'New Customer' : 'Existing Customer'}
                </span>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 5 }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9f9f9", borderBottom: "1px solid #ddd" }}>
                    <th style={{ textAlign: "left", padding: 4, fontSize: 8, color: "#000", width: 30 }}>#</th>
                    <th style={{ textAlign: "left", padding: 4, fontSize: 8, color: "#000" }}>Item Name</th>
                    <th style={{ textAlign: "center", padding: 4, fontSize: 8, color: "#000", width: 80 }}>SKU/Code</th>
                    <th style={{ textAlign: "center", padding: 4, fontSize: 8, color: "#000", width: 80 }}>Category</th>
                    <th style={{ textAlign: "center", padding: 4, fontSize: 8, color: "#000", width: 40 }}>Qty</th>
                    <th style={{ textAlign: "right", padding: 4, fontSize: 8, color: "#000", width: 70 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(sale.items || []).map((item, itemIdx) => (
                    <tr key={itemIdx} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: 3, fontSize: 8, color: "#000" }}>{itemIdx + 1}</td>
                      <td style={{ padding: 3, fontSize: 8, color: "#000" }}>
                        {item.title || item.name || 'Unnamed Item'}
                      </td>
                      <td style={{ padding: 3, fontSize: 8, color: "#666", textAlign: "center" }}>
                        {item.sku || item.code || '-'}
                      </td>
                      <td style={{ padding: 3, fontSize: 8, color: "#666", textAlign: "center" }}>
                        {item.category || item.subCategory || '-'}
                      </td>
                      <td style={{ padding: 3, fontSize: 8, color: "#000", textAlign: "center" }}>
                        {item.qty || item.quantity || 1}
                      </td>
                      <td style={{ padding: 3, fontSize: 8, color: "#000", textAlign: "right" }}>
                        {item.price ? formatCurrency(item.price * (item.qty || item.quantity || 1)) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: APP_COLOURS.BG }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
          <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading productivity sheet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: APP_COLOURS.BG }}>
        <div className="text-center max-w-md">
          <i className="fas fa-exclamation-triangle text-6xl mb-4" style={{ color: APP_COLOURS.ERROR }}></i>
          <h2 className="text-2xl font-bold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            Unable to Load Productivity Sheet
          </h2>
          <p style={{ color: APP_COLOURS.PLACEHOLDER }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!productivityData) return null;

  const { staff, period, salesMetrics, attendanceMetrics, sales } = productivityData;

  const canSign = productivityData.commission.status !== 'cancelled' && productivityData.commission.status !== 'completed';

  return (
    <div style={{ backgroundColor: "#fff", minHeight: '100vh', padding: 20 }}>
      {/* Action Buttons and Shareable Link - No Print */}
      <div className="no-print" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h1 style={{ fontSize: 24, fontWeight: 'bold', color: '#000', margin: 0 }}>
            Productivity Sheet
          </h1>
          <button
            onClick={handlePrint}
            style={{
              backgroundColor: APP_COLOURS.BLUE,
              color: 'white',
              padding: '10px 20px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <i className="fas fa-print" style={{ marginRight: 8 }}></i>
            Print
          </button>
        </div>

        {/* Shareable Link Box */}
        <div style={{
          backgroundColor: '#f0f8ff',
          border: '1px solid #0068ff',
          borderRadius: 8,
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 11, color: '#666', margin: 0, marginBottom: 4 }}>
              Shareable Link (expires in 30 days):
            </p>
            <input
              type="text"
              value={window.location.href}
              readOnly
              style={{
                width: '100%',
                padding: 8,
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid #ddd',
                backgroundColor: '#fff',
                color: '#000',
              }}
            />
          </div>
          <button
            onClick={handleCopyLink}
            style={{
              backgroundColor: APP_COLOURS.GREEN,
              color: 'white',
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
            Copy Link
          </button>
        </div>
      </div>

      <div id="productivity-sheet" style={{ maxWidth: 900, margin: '0 auto', backgroundColor: '#fff', padding: 12 }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          borderBottom: "1px solid #000",
          paddingBottom: 6,
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>TRAQ</div>
            <div style={{ fontSize: 12, fontWeight: "bold", color: "#000" }}>PRODUCTIVITY SHEET</div>
          </div>
          <div>
            <div style={{ color: "#000", fontSize: 12, fontWeight: "bold" }}>{period.title}</div>
            <div style={{ color: "#000", fontSize: 10 }}>
              Period: {moment(period.startDate).format("DD/MM/YYYY")} - {moment(period.endDate).format("DD/MM/YYYY")}
            </div>
          </div>
        </div>

        {/* Staff Info */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
          padding: 6,
          backgroundColor: "#f5f5f5",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#000", fontWeight: "bold", fontSize: 11 }}>Employee Name:</div>
            <div style={{ color: "#000", marginBottom: 5, fontSize: 11 }}>{staff.first_name} {staff.last_name}</div>
            <div style={{ color: "#000", fontWeight: "bold", fontSize: 11 }}>Position:</div>
            <div style={{ color: "#000", fontSize: 11 }}>{staff.position || "N/A"}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#000", fontWeight: "bold", fontSize: 11 }}>ID Number:</div>
            <div style={{ color: "#000", marginBottom: 5, fontSize: 11 }}>{staff.id}</div>
            <div style={{ color: "#000", fontWeight: "bold", fontSize: 11 }}>Email:</div>
            <div style={{ color: "#000", fontSize: 11 }}>{staff.email || "N/A"}</div>
          </div>
        </div>

        {/* Performance Summary */}
        <div style={{ marginBottom: 10 }}>
          <div style={{
            fontSize: 14,
            fontWeight: "bold",
            marginBottom: 5,
            color: "#000",
            borderBottom: "1px solid #ccc",
            paddingBottom: 3,
          }}>
            Performance Summary
          </div>

          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <div style={{ width: "25%", marginBottom: 5 }}>
              <div style={{ fontWeight: "bold", color: "#000", fontSize: 10 }}>Sales Performance</div>
              <div style={{ marginTop: 2 }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Target:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{formatCurrency(salesMetrics.targetAmount)}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Actual Sales:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{formatCurrency(salesMetrics.actualSales)}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Achievement:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{salesMetrics.percToTarget.toFixed(1)}%</div>
                </div>
              </div>
            </div>

            <div style={{ width: "25%", marginBottom: 5 }}>
              <div style={{ fontWeight: "bold", color: "#000", fontSize: 10 }}>Transactions</div>
              <div style={{ marginTop: 2 }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Total Trans.:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{salesMetrics.totalTransactions}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Avg. Sale Value:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{formatCurrency(salesMetrics.averageArs)}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Total Units:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{salesMetrics.totalUnits.toFixed(0)}</div>
                </div>
              </div>
            </div>

            <div style={{ width: "25%", marginBottom: 5 }}>
              <div style={{ fontWeight: "bold", color: "#000", fontSize: 10 }}>Attendance</div>
              <div style={{ marginTop: 2 }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Days Present:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{attendanceMetrics.totalDaysPresent}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Total Hours:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{attendanceMetrics.totalHours.toFixed(1)}h</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Avg. Hours/Day:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{attendanceMetrics.averageHoursPerDay.toFixed(1)}h</div>
                </div>
              </div>
            </div>

            <div style={{ width: "25%", marginBottom: 5 }}>
              <div style={{ fontWeight: "bold", color: "#000", fontSize: 10 }}>Productivity</div>
              <div style={{ marginTop: 2 }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Avg. Items/Trans.:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{salesMetrics.averageItp.toFixed(2)}</div>
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "65%", color: "#000", fontSize: 9 }}>Sales Records:</div>
                  <div style={{ color: "#000", fontSize: 9 }}>{sales.length}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Performance Table */}
        <div style={{ marginBottom: 10 }}>
          <div style={{
            fontSize: 14,
            fontWeight: "bold",
            marginBottom: 5,
            color: "#000",
            borderBottom: "1px solid #ccc",
            paddingBottom: 3,
          }}>
            Daily Performance Summary
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0", borderBottom: "1px solid #ccc" }}>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Date</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Day</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Check In</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Check Out</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Hours</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>New Cust.</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>Exist. Cust.</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>TRS</th>
                <th style={{ padding: 4, fontWeight: "bold", color: "#000", fontSize: 9, textAlign: "left" }}>ARS</th>
              </tr>
            </thead>
            <tbody>
              {renderDailyPerformanceRows()}
            </tbody>
          </table>
        </div>

        {/* Detailed Audit Trail */}
        <div style={{ marginTop: 20, pageBreakBefore: "always" }}>
          <div style={{
            fontSize: 14,
            fontWeight: "bold",
            marginBottom: 10,
            color: "#000",
            borderBottom: "1px solid #ccc",
            paddingBottom: 3,
          }}>
            Detailed Sales Audit Trail
          </div>
          {renderAuditTrail()}
        </div>

        {/* Signatures */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 20,
          borderTop: "1px solid #ccc",
          paddingTop: 15,
        }}>
          <div style={{ width: "45%" }}>
            <div style={{ 
              borderBottom: "1px solid #000", 
              paddingBottom: 20,
              minHeight: 50,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}>
              {productivityData.commission.staffSignature && (
                <div style={{ textAlign: 'center', width: '100%' }}>
                  {productivityData.commission.staffSignatureImage && (
                    <img 
                      src={productivityData.commission.staffSignatureImage} 
                      alt="Staff Signature"
                      style={{ maxHeight: 40, marginBottom: 5, display: 'block', margin: '0 auto' }}
                    />
                  )}
                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#000' }}>
                    {productivityData.commission.staffSignature}
                  </div>
                  <div style={{ fontSize: 9, color: '#666' }}>
                    {moment(productivityData.commission.staffSignatureDate).format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: 5, textAlign: "center", color: "#000", fontSize: 10 }}>
              Staff Signature
            </div>
            {!productivityData.commission.staffSignature && canSign && (
              <div className="no-print" style={{ marginTop: 8, textAlign: 'center' }}>
                <button
                  onClick={() => handleOpenSignatureModal('staff')}
                  style={{
                    backgroundColor: APP_COLOURS.BLUE,
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  Sign as Staff
                </button>
              </div>
            )}
          </div>

          <div style={{ width: "45%" }}>
            <div style={{ 
              borderBottom: "1px solid #000", 
              paddingBottom: 20,
              minHeight: 50,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}>
              {productivityData.commission.managerSignature && (
                <div style={{ textAlign: 'center', width: '100%' }}>
                  {productivityData.commission.managerSignatureImage && (
                    <img 
                      src={productivityData.commission.managerSignatureImage} 
                      alt="Manager Signature"
                      style={{ maxHeight: 40, marginBottom: 5, display: 'block', margin: '0 auto' }}
                    />
                  )}
                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#000' }}>
                    {productivityData.commission.managerSignature}
                  </div>
                  <div style={{ fontSize: 9, color: '#666' }}>
                    {moment(productivityData.commission.managerSignatureDate).format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: 5, textAlign: "center", color: "#000", fontSize: 10 }}>
              Manager Signature
            </div>
            {!productivityData.commission.managerSignature && canSign && (
              <div className="no-print" style={{ marginTop: 8, textAlign: 'center' }}>
                <button
                  onClick={() => handleOpenSignatureModal('manager')}
                  style={{
                    backgroundColor: APP_COLOURS.ORANGE,
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  Sign as Manager
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 10,
          borderTop: "1px solid #eee",
          paddingTop: 5,
          textAlign: "center",
          color: "#555",
          fontSize: 8,
        }}>
          Staff Productivity Report • Generated {moment().format("DD/MM/YYYY HH:mm")}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowSignatureModal(false)}
        >
          <div
            style={{
              backgroundColor: APP_COLOURS.BG2,
              borderRadius: 12,
              padding: 30,
              maxWidth: 500,
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 20, fontWeight: 'bold', color: APP_COLOURS.WHITE, marginBottom: 10 }}>
              {signatureType === 'manager' ? 'Manager Signature' : 'Staff Signature'}
            </h3>
            <p style={{ fontSize: 14, color: APP_COLOURS.PLACEHOLDER, marginBottom: 20 }}>
              I acknowledge this productivity sheet and confirm the accuracy of the information provided.
            </p>

            <label style={{ fontSize: 13, color: APP_COLOURS.PLACEHOLDER, display: 'block', marginBottom: 5 }}>
              Full Name *
            </label>
            <input
              type="text"
              placeholder="Type your full name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: 12,
                fontSize: 16,
                borderRadius: 6,
                border: `1px solid ${APP_COLOURS.BG4}`,
                backgroundColor: APP_COLOURS.BG3,
                color: APP_COLOURS.WHITE,
                marginBottom: 15,
              }}
            />

            <label style={{ fontSize: 13, color: APP_COLOURS.PLACEHOLDER, display: 'block', marginBottom: 5 }}>
              Draw Your Signature *
            </label>
            <div style={{
              backgroundColor: '#fff',
              borderRadius: 6,
              padding: 10,
              marginBottom: 10,
            }}>
              <canvas
                ref={initCanvas}
                width={440}
                height={150}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                  border: '2px dashed #ddd',
                  borderRadius: 4,
                  cursor: 'crosshair',
                  width: '100%',
                  display: 'block',
                }}
              />
              <div style={{ textAlign: 'right', marginTop: 5 }}>
                <button
                  onClick={clearSignature}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#666',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    textDecoration: 'underline',
                  }}
                >
                  Clear Signature
                </button>
              </div>
            </div>

            <div style={{ fontSize: 12, color: APP_COLOURS.PLACEHOLDER, marginBottom: 20, textAlign: 'center' }}>
              {moment().format('DD MMMM YYYY, HH:mm')}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowSignatureModal(false)}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: APP_COLOURS.WHITE,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSignature}
                disabled={saving || !signatureName.trim() || !signatureData}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 6,
                  border: 'none',
                  cursor: saving || !signatureName.trim() || !signatureData ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  backgroundColor: APP_COLOURS.BLUE,
                  color: 'white',
                  opacity: saving || !signatureName.trim() || !signatureData ? 0.6 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Sign & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProductivitySheet;

